<script lang="ts" module>
	import { attrs } from '#library/attrs.ts'
	import type { Blok, Product, Products } from '#library/components.schema.build.ts'
	import Render from '#components/render.svelte'
	import Image from '#components/image.svelte'
	import Button from '#components/button.svelte'
	import Modal from '#components/modal.svelte'
	import Checkbox from '#components/checkbox.svelte'
	import Field from '#components/field.svelte'
	import { format_string } from 'kitto'
	import { enhance } from '$app/forms'
	import { page } from '$app/state'
	import { goto } from '$app/navigation'
	import { params } from '#library/params.svelte.ts'
	import Captcha from 'postboi/svelte'

	let products: Array<Product> | undefined = $state()
</script>

<script lang="ts">
	interface Props {
		blok: Blok<Products>
	}

	let { blok }: Props = $props()

	let open = $state(false)
	let step = $state(0)
	let form_state: 'idle' | 'submitting' | 'success' | 'error' = $state('idle')
	let selected_product_slugs: Array<string> = $derived(page.url.searchParams.get('products')?.split(',') || [])

	let form_data = $state({
		name: '',
		email: '',
		message: '',
		terms: []
	})

	type SubmitFunction = Parameters<typeof enhance>[1]

	const submit: SubmitFunction = () => {
		form_state = 'submitting'

		return async ({ result }) => {
			switch (result.type) {
				case 'success':
					form_state = 'success'
					open = false
					step = 0
					selected_product_slugs = []
					await goto(page.url.pathname, { replace: true, reset: false })
					break

				case 'error':
				case 'failure':
					step = 0
					form_state = 'error'
					break
			}
		}
	}

	const toGBP = (n: string | undefined) => `£${n?.replace('.00', '')}`

	products = [...(products || []), ...(blok?.items || [])]

	const product_checkboxes = $derived(
		products
			?.filter(p => !p.hide_order_button)
			.map(({ title, price }) => {
				const value = format_string(title as never, 'slug' as never)
				const label = `${title} ${toGBP(price)}`

				return { id: value, value, label }
			})
	)

	const selected_products = $derived(product_checkboxes?.filter(p => selected_product_slugs.includes(p.value)))

	async function order(uid: string) {
		open = true

		await goto(params({ products: [...selected_product_slugs, uid].join(',') }), {
			replace: true,
			reset: false
		})
	}

	async function onclose() {
		open = false
		step = 0

		await goto(page.url.pathname, { replace: true, reset: false })
	}
</script>

{#snippet Product({ title, body, price, image, hide_order_button }: Product)}
	{@const value = format_string(title as never, 'slug' as never)}
	<div class="product" class:feature={blok.feature}>
		{#if image?.filename}
			<Image src={image.filename} alt={image.alt} focus={image.focus} aspect="6/5" />
		{/if}
		<div class="content">
			<div class="name">{title}</div>
			{#if body?.content?.[0]?.content}
				<div class="desc"><Render content={body} /></div>
			{/if}
			<div class="price">{toGBP(price)}</div>
			{#if !hide_order_button}
				<Button onclick={() => order(value)}>Place an order</Button>
			{/if}
		</div>
	</div>
{/snippet}

<section class="products {blok.theme || 'theme-crocus-on-white'}" {...attrs(blok)}>
	<div class="contain">
		<div class="title">{blok.title}</div>
		{#if blok.body?.content?.[0]?.content}
			<div class="body">
				<Render content={blok.body} />
			</div>
		{/if}
		<div class="items">
			{#each blok.items || [] as item, i (i)}
				{@render Product(item)}
			{/each}
		</div>
	</div>
</section>

{#if open}
	<Modal title="Item order" close={onclose} {open}>
		<div class="modal_subtitle">All orders will be available for payment and collection at Reception.</div>
		<div class="modal_body">
			If you are unable to collect, please let us know in the 'Comment on your order' section.
		</div>
		{#if step === 0}
			<Checkbox
				name="items"
				label="Select item(s) you are interested in:"
				options={product_checkboxes || []}
				at_least_one
				bind:value={
					() => selected_product_slugs,
					v => {
						goto(params({ products: v.join(',') }), { replace: true, reset: false })
						selected_product_slugs = v
					}
				}
			/>
			<br />
			<Button
				type="button"
				onclick={() => (step = 1)}
				icon="next"
				fill
				disabled={selected_products?.length === 0}>Next</Button
			>
		{:else}
			<form action="/?/products" class="form" method="post" use:enhance={submit}>
				{#each selected_products as { label: value }, i (i)}
					<input type="hidden" name="Products" {value} />
				{/each}
				<Field
					name="Name"
					id="name"
					label="What's your name?"
					placeholder="Enter your name"
					bind:value={form_data.name}
				/>
				<Field
					name="Email"
					id="email"
					label="Your email (so we can get back to you)"
					placeholder="Enter your email"
					bind:value={form_data.email}
					required
				/>
				<Field
					type="textarea"
					name="Message"
					id="message"
					label="Comment on your order"
					placeholder="e.g. I'd like a half-pint mug in 'Stormy Blue'"
					bind:value={form_data.message}
					required
					rows={5}
				/>
				<Checkbox
					name="Agreed to T&Cs and Privacy Policy"
					options={[
						{
							id: 'terms',
							value: 'Agreed',
							label: 'I have read and understood the terms & conditions & privacy policy',
							required: true
						}
					]}
					bind:value={form_data.terms}
				/>
				<Captcha />
				<div class="buttons">
					<Button type="button" onclick={() => (step = 0)} icon="arrow-left" outline>Back</Button>
					<Button type="submit" fill loading={form_state === 'submitting'}>Place order</Button>
				</div>
			</form>
		{/if}
	</Modal>
{/if}

{#if form_state === 'success'}
	<Modal
		title="Thank you for your order"
		close={() => ((form_state = 'idle'), (step = 0))}
		open={form_state === 'success'}
		dialog
	>
		<div class="subtitle">A member of our team will be in touch as soon as possible.</div>
		<Button type="button" fill outline onclick={() => ((form_state = 'idle'), (step = 0))}>Close</Button>
	</Modal>
{/if}

<style lang="css">
	.products {
		background-color: var(--c-background);
		color: var(--c-foreground);
		padding-block: var(--padding-top, var(--padding-default)) var(--padding-bottom, var(--padding-default));
	}

	.contain {
	}

	.title {
		color: var(--c-foreground);
		font: 600 var(--f-h4);
		letter-spacing: var(--ls-h4);
		margin-block-end: 1rem;
	}

	.body {
		font: 400 var(--f-body2);
		letter-spacing: var(--ls-body2);
		max-width: 70ch;
	}

	.items {
		display: grid;
		gap: 1rem;
		margin-block-start: 2.5rem;

		@media (--from-tablet) {
			gap: 2.5rem;
			grid-template-columns: repeat(3, minmax(0, 1fr));
		}
	}

	.product {
		background-color: var(--c-gimson-light);
		display: flex;
		flex-direction: column;
		gap: 1rem;
		padding: 1.5rem;

		&.feature {
			@media (--from-tablet) {
				display: grid;
				gap: 3rem;
				grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
				grid-column: 1 / -1;
				padding: 3.5rem;
			}
		}

		:global(.image) {
			margin-block-end: 0.5rem;
		}
	}

	.content {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		flex: 1;

		:global(.button) {
			max-width: 22rem;
		}
	}

	.name {
		font: 600 var(--f-h4);
		letter-spacing: var(--ls-h4);
	}

	.desc {
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
		flex: 1;

		:global(p:first-child) {
			margin-block-start: 0;
		}
	}

	.price {
		font: 600 var(--f-h5);
		letter-spacing: var(--ls-h5);
	}

	.modal_subtitle {
		font: 500 var(--f-body1);
		letter-spacing: var(--ls-body1);
	}

	.modal_body {
		font: 400 var(--f-body2);
		letter-spacing: var(--ls-body2);
		margin-block: 0.5rem 1.5rem;
	}

	.form {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.buttons {
		display: flex;
		gap: 1rem;
	}
</style>
